import React from 'react'
import styled from 'styled-components'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'

export const CONCEPT = {
  obligation: '#1f8c97',
  task:       '#33becc',
  remedial:   '#D24724',
}

const INPUT_W = 212
const ARROW_W = 42
const BOX_W = 234
const OFFSET = INPUT_W + ARROW_W

const CONCEPTS = {
  obligation: {
    label: 'Obligation',
    tint: CONCEPT.obligation,
    Icon: FactCheckOutlinedIcon,
    desc: 'what a building must do',
    detail: 'The requirement. A recurring legal or contractual duty such as a valid EICR on file or an annual fire-door inspection. It is what your compliance score measures, met by a current document (evidence) or a completed task.',
  },
  task: {
    label: 'Task',
    tint: CONCEPT.task,
    Icon: AssignmentTurnedInOutlinedIcon,
    desc: 'the work that does it',
    detail: 'The planned work you carry out and record. The routine that meets an obligation, or the corrective work that closes a remedial. Completing it on time is what keeps things satisfied.',
  },
  remedial: {
    label: 'Remedial',
    tint: CONCEPT.remedial,
    Icon: HandymanOutlinedIcon,
    desc: 'a problem to put right',
    detail: 'A deficiency or overdue item to fix. Surfaced from a document, an incident or accident, or a completed task’s form. You close it by doing a task.',
  },
}

const OBLIGATION_INPUTS = [
  { Icon: GavelRoundedIcon, label: 'Compliance & building ops' },
  { Icon: DescriptionOutlinedIcon, label: 'Documents (evidence)' },
]
const REMEDIAL_INPUTS = [
  { Icon: DescriptionOutlinedIcon, label: 'Documents' },
  { Icon: ReportProblemOutlinedIcon, label: 'Incidents & accidents' },
]

const ConceptCard = ({ k }) => {
  const c = CONCEPTS[k]
  const { Icon } = c
  return (
    <Card $tint={c.tint} tabIndex={0}>
      <IconTile $tint={c.tint}><Icon style={{ fontSize: 22 }} /></IconTile>
      <CardText>
        <CardTitle $tint={c.tint}>{c.label}</CardTitle>
        <CardDesc>{c.desc}</CardDesc>
      </CardText>
      <HoverCard $tint={c.tint}>
        <HoverTitle $tint={c.tint}>{c.label}</HoverTitle>
        {c.detail}
      </HoverCard>
    </Card>
  )
}

const InputCol = ({ items }) => (
  <Inputs>
    <InputsLead>raised / defined by</InputsLead>
    {items.map(it => {
      const FI = it.Icon
      return (
        <Chip key={it.label}>
          <FI style={{ fontSize: 15 }} />
          {it.label}
        </Chip>
      )
    })}
  </Inputs>
)

const ConceptModelDiagram = ({ className }) => (
  <Frame className={className}>
    <Legend>
      <LegendItem><Sample /> usual path</LegendItem>
      <LegendItem><Sample $dotted $tint={CONCEPT.task} /> a task can raise a remedial</LegendItem>
      <LegendHint>Hover a card for more</LegendHint>
    </Legend>

    <Flow>
      <Lane>
        <InputCol items={OBLIGATION_INPUTS} />
        <HArrow><HLine /></HArrow>
        <ConceptCard k="obligation" />
      </Lane>

      <VConn>
        <VLine $tint={CONCEPT.obligation} />
        <VLabel $tint={CONCEPT.obligation}>do the work</VLabel>
      </VConn>

      <Lane>
        <Spacer />
        <ConceptCard k="task" />
      </Lane>

      <VConn>
        <VLine $tint={CONCEPT.task} $dotted />
        <VLabel $tint={CONCEPT.task}>can raise</VLabel>
      </VConn>

      <Lane>
        <InputCol items={REMEDIAL_INPUTS} />
        <HArrow><HLine /></HArrow>
        <ConceptCard k="remedial" />
        <HArrow $w={66}>
          <HLabel $tint={CONCEPT.remedial}>fix it</HLabel>
          <HLine $tint={CONCEPT.remedial} />
        </HArrow>
        <ConceptCard k="task" />
      </Lane>
    </Flow>

    <Caption>Doing the task keeps the obligation satisfied and closes the remedial.</Caption>
  </Frame>
)

export default ConceptModelDiagram

/*
 * The diagram is a horizontal flow built from fixed-width lanes, which cannot
 * fit a phone. Below MOBILE it folds into a single vertical column: inputs
 * above the card they feed, arrows turned to point down.
 */
const MOBILE = '768px'

const Frame = styled.div`
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  width: 100%;
  min-width: 0;

  @media (max-width: ${MOBILE}) { padding: 14px 12px; }
`

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Sample = styled.span`
  width: 24px;
  border-top: 2px ${({ $dotted }) => ($dotted ? 'dashed' : 'solid')} ${({ $tint }) => $tint || '#8a93a3'};
`

const LegendHint = styled.span`
  margin-left: auto;
  font-size: 11.5px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[800]};

  /* Hovering is not a thing on touch. */
  @media (max-width: ${MOBILE}) { display: none; }
`

const Flow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: fit-content;
  margin: 0 auto;
  max-width: 100%;

  @media (max-width: ${MOBILE}) {
    width: 100%;
    align-items: stretch;
  }
`

const Lane = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: ${MOBILE}) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    width: 100%;
  }
`

const Spacer = styled.div`
  width: ${OFFSET}px;
  flex-shrink: 0;

  @media (max-width: ${MOBILE}) { display: none; }
`

const Inputs = styled.div`
  box-sizing: border-box;
  width: ${INPUT_W}px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;

  /* Stacked layout: the chips read better side by side under their lead. */
  @media (max-width: ${MOBILE}) {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
  }
`

const InputsLead = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};

  @media (max-width: ${MOBILE}) { flex: 1 1 100%; }
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  color: #475569;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  padding: 7px 11px;
  svg { color: #6b7280; flex-shrink: 0; }

  @media (max-width: ${MOBILE}) {
    white-space: normal;
    max-width: 100%;
    min-width: 0;
    flex: 0 1 auto;
    font-size: 12px;
    padding: 6px 9px;
  }
`

const HArrow = styled.div`
  position: relative;
  width: ${({ $w }) => $w || ARROW_W}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media (max-width: ${MOBILE}) {
    width: 100%;
    height: 28px;
    justify-content: center;
  }
`

const Card = styled.div`
  box-sizing: border-box;
  position: relative;
  width: ${BOX_W}px;
  min-height: 78px;
  flex-shrink: 0;

  @media (max-width: ${MOBILE}) { width: 100%; }
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 15px;
  border-radius: 12px;
  background: ${({ $tint }) => `${$tint}10`};
  border: 1.5px solid ${({ $tint }) => `${$tint}45`};
  outline: none;
  cursor: default;
  transition: background-color 150ms ease, box-shadow 150ms ease;
  &:hover, &:focus-visible {
    background: ${({ $tint }) => `${$tint}1f`};
    box-shadow: 0 1px 3px rgba(12, 18, 41, 0.07);
  }
`

const IconTile = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  color: ${({ $tint }) => $tint};
  background: ${({ $tint }) => `${$tint}1f`};
`

const CardText = styled.div`
  min-width: 0;
`

const CardTitle = styled.div`
  font-size: 16.5px;
  font-weight: 800;
  line-height: 1.15;
  color: ${({ $tint }) => $tint};
`

const CardDesc = styled.div`
  font-size: 13px;
  line-height: 1.3;
  margin-top: 1px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const HoverCard = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(-4px);
  width: 270px;
  max-width: 88vw;
  z-index: 6;
  padding: 11px 13px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid ${({ $tint }) => `${$tint}40`};
  box-shadow: 0 12px 30px rgba(12, 18, 41, 0.18);
  font-size: 12.5px;
  line-height: 1.5;
  color: #4d4d56;
  opacity: 0;
  visibility: hidden;
  transition: opacity 140ms ease, transform 140ms ease, visibility 140ms;
  ${Card}:hover &, ${Card}:focus-visible & {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
`

const HoverTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 3px;
  color: ${({ $tint }) => $tint};
`

const VConn = styled.div`
  position: relative;
  margin-left: ${OFFSET}px;
  width: ${BOX_W}px;
  height: 42px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: ${MOBILE}) {
    margin-left: 0;
    width: 100%;
    height: 36px;
  }
`

const VLine = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  width: 10px;
  height: 100%;
  &::before {
    content: '';
    flex: 1;
    width: 0;
    border-left: 2px ${({ $dotted }) => ($dotted ? 'dashed' : 'solid')} ${({ $tint }) => $tint};
    opacity: 0.9;
  }
  &::after {
    content: '';
    width: 0; height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 8px solid ${({ $tint }) => $tint};
  }
`

const VLabel = styled.span`
  position: absolute;
  left: calc(50% + 12px);
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  font-size: 11.5px;
  font-style: italic;
  color: ${({ $tint }) => $tint};
`

const HLine = styled.span`
  display: inline-flex;
  align-items: center;
  width: 100%;
  height: 10px;
  &::before {
    content: '';
    flex: 1;
    height: 0;
    border-top: 2px solid ${({ $tint }) => $tint || '#aeb6c2'};
    opacity: 0.9;
  }
  &::after {
    content: '';
    width: 0; height: 0;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 8px solid ${({ $tint }) => $tint || '#aeb6c2'};
  }

  /* Turned into a downward arrow for the stacked layout. */
  @media (max-width: ${MOBILE}) {
    flex-direction: column;
    width: 10px;
    height: 100%;

    &::before {
      width: 0;
      height: auto;
      border-top: none;
      border-left: 2px solid ${({ $tint }) => $tint || '#aeb6c2'};
    }

    &::after {
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 8px solid ${({ $tint }) => $tint || '#aeb6c2'};
      border-bottom: none;
    }
  }
`

const HLabel = styled.span`
  position: absolute;
  bottom: calc(50% + 5px);
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 11.5px;
  font-style: italic;
  color: ${({ $tint }) => $tint};

  @media (max-width: ${MOBILE}) {
    bottom: auto;
    top: 50%;
    left: calc(50% + 14px);
    transform: translateY(-50%);
  }
`

const Caption = styled.div`
  margin-top: 16px;
  @media (max-width: ${MOBILE}) { margin-top: 12px; }
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
`
